import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Eye,
  EyeOff,
  HelpCircle,
  Loader2,
  Pencil,
  RefreshCw,
  Trash2,
  WifiOff,
  XCircle,
  Link2,
  FolderSync,
} from 'lucide-react';
import {
  apiClient,
  type AcervoImportResponse,
  type AcervoPreviewResponse,
  type ApiError,
  type CreateTribunalConnectionInput,
  type TribunalAdapterStatus,
  type TribunalConnectionView,
  type UpdateTribunalConnectionInput,
} from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Field, GlassSelect, TextInput } from '@/components/clients/form-primitives';
import { ConfigModal } from './config-modal';

// ---------------------------------------------------------------------------
// Catálogo estático de sistemas e tribunais
// ---------------------------------------------------------------------------

interface CatalogTribunal {
  tribunalCode: string;
  grau: 1 | 2;
  label: string;
}

interface CatalogSistema {
  id: 'eproc' | 'pje' | 'jpe' | 'esaj' | 'projudi';
  label: string;
  enabled: boolean;
  tribunais: CatalogTribunal[];
}

const CATALOG: CatalogSistema[] = [
  {
    id: 'eproc',
    label: 'eProc',
    enabled: true,
    tribunais: [
      { tribunalCode: 'TJRS', grau: 1, label: 'TJRS — 1º Grau' },
      { tribunalCode: 'TJRS', grau: 2, label: 'TJRS — 2º Grau' },
      { tribunalCode: 'TJSC', grau: 1, label: 'TJSC — 1º Grau' },
      { tribunalCode: 'TJSC', grau: 2, label: 'TJSC — 2º Grau' },
      { tribunalCode: 'TRF4', grau: 1, label: 'TRF4 — 1º Grau' },
      { tribunalCode: 'TRF4', grau: 2, label: 'TRF4 — 2º Grau' },
      { tribunalCode: 'TRF2', grau: 1, label: 'TRF2 — 1º Grau' },
      { tribunalCode: 'TRF2', grau: 2, label: 'TRF2 — 2º Grau' },
      { tribunalCode: 'TRF6', grau: 1, label: 'TRF6 — 1º Grau' },
      { tribunalCode: 'TRF6', grau: 2, label: 'TRF6 — 2º Grau' },
      { tribunalCode: 'TJTO', grau: 1, label: 'TJTO — 1º Grau' },
      { tribunalCode: 'TJTO', grau: 2, label: 'TJTO — 2º Grau' },
      { tribunalCode: 'TJAC', grau: 1, label: 'TJAC — 1º Grau' },
      { tribunalCode: 'TJAC', grau: 2, label: 'TJAC — 2º Grau' },
    ],
  },
  {
    id: 'pje',
    label: 'PJe',
    enabled: true,
    tribunais: [
      { tribunalCode: 'TJRJ', grau: 1, label: 'TJRJ — 1º Grau' },
      { tribunalCode: 'TJRJ', grau: 2, label: 'TJRJ — 2º Grau' },
      { tribunalCode: 'TJMG', grau: 1, label: 'TJMG — 1º Grau' },
      { tribunalCode: 'TJBA', grau: 1, label: 'TJBA — 1º Grau' },
      { tribunalCode: 'TJCE', grau: 1, label: 'TJCE — 1º Grau' },
      { tribunalCode: 'TRT6', grau: 1, label: 'TRT6 — 1º Grau' },
    ],
  },
  {
    // JPe = sistema PROPRIO do TJMG para 2o grau, DISTINTO do PJe Cloud do 1o
    // grau (mesmo tribunal, sistemas diferentes por grau — nome parecido e
    // intencional do TJMG, nao e erro de digitacao). Ver JpeConfigService.
    id: 'jpe',
    label: 'JPe',
    enabled: true,
    tribunais: [
      { tribunalCode: 'TJMG', grau: 2, label: 'TJMG — 2º Grau' },
    ],
  },
  {
    id: 'esaj',
    label: 'eSAJ',
    enabled: false,
    tribunais: [
      { tribunalCode: 'TJSP', grau: 1, label: 'TJSP — 1º Grau' },
      { tribunalCode: 'TJSP', grau: 2, label: 'TJSP — 2º Grau' },
    ],
  },
  {
    id: 'projudi',
    label: 'Projudi',
    enabled: false,
    tribunais: [
      { tribunalCode: 'TJPR', grau: 1, label: 'TJPR — 1º Grau' },
      { tribunalCode: 'TJMT', grau: 1, label: 'TJMT — 1º Grau' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Campos de formulário
// ---------------------------------------------------------------------------

const UF_OPTIONS: { value: string; label: string }[] = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
].map((uf) => ({ value: uf, label: uf }));

// ---------------------------------------------------------------------------
// Helpers de status
// ---------------------------------------------------------------------------

interface StatusMeta {
  label: string;
  colorClass: string;
  icon: typeof CheckCircle2;
  severity: 'ok' | 'warn' | 'error';
}

function getStatusMeta(status: TribunalAdapterStatus | 'pending' | null): StatusMeta {
  if (status === null) {
    return {
      label: 'Não conectado',
      colorClass: 'bg-zinc-500/15 text-zinc-400',
      icon: WifiOff,
      severity: 'warn',
    };
  }
  switch (status) {
    case 'active':
      return { label: 'Ativo', colorClass: 'bg-emerald-500/15 text-emerald-500', icon: CheckCircle2, severity: 'ok' };
    case 'pending':
      return { label: 'Pendente', colorClass: 'bg-amber-500/15 text-amber-500', icon: Clock, severity: 'warn' };
    case 'session_expired':
      return { label: 'Sessão expirada', colorClass: 'bg-amber-500/15 text-amber-500', icon: Clock, severity: 'warn' };
    case 'invalid_password':
      return { label: 'Senha inválida', colorClass: 'bg-red-500/15 text-red-500', icon: XCircle, severity: 'error' };
    case 'invalid_2fa':
      return { label: '2FA inválido', colorClass: 'bg-red-500/15 text-red-500', icon: XCircle, severity: 'error' };
    case 'disconnected':
      return { label: 'Desconectado', colorClass: 'bg-zinc-500/15 text-zinc-400', icon: WifiOff, severity: 'warn' };
    case 'error':
      return { label: 'Erro', colorClass: 'bg-red-500/15 text-red-500', icon: AlertTriangle, severity: 'error' };
  }
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function connectionLabel(conn: TribunalConnectionView): string {
  return `${conn.sistema.toUpperCase()} ${conn.tribunalCode} ${conn.grau}º Grau`;
}

// ---------------------------------------------------------------------------
// Formulário
// ---------------------------------------------------------------------------

interface FormState {
  sistema: 'eproc' | 'pje' | 'jpe' | 'esaj' | 'projudi';
  tribunalCode: string;
  grau: '1' | '2';
  oabUf: string;
  oabNumber: string;
  loginUsername: string;
  senha: string;
  totpSeed: string;
  showSenha: boolean;
  showTotp: boolean;
}

const EMPTY_FORM: FormState = {
  sistema: 'eproc',
  tribunalCode: '',
  grau: '1',
  oabUf: 'RS',
  oabNumber: '',
  loginUsername: '',
  senha: '',
  totpSeed: '',
  showSenha: false,
  showTotp: false,
};

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Estado do formulário de edição
// ---------------------------------------------------------------------------

interface EditFormState {
  oabUf: string;
  oabNumber: string;
  loginUsername: string;
  senha: string;
  totpSeed: string;
  showSenha: boolean;
  showTotp: boolean;
}

function emptyEditForm(conn: TribunalConnectionView): EditFormState {
  return {
    oabUf: conn.oabUf,
    oabNumber: conn.oabNumber,
    loginUsername: conn.loginUsername ?? '',
    senha: '',
    totpSeed: '',
    showSenha: false,
    showTotp: false,
  };
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export function TribunalConnectionsPage() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formLocked, setFormLocked] = useState<{ sistema: boolean; tribunalCode: boolean; grau: boolean }>({
    sistema: false,
    tribunalCode: false,
    grau: false,
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'ok' | 'error'; message: string } | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [acervoLoadingId, setAcervoLoadingId] = useState<string | null>(null);
  const [acervoResult, setAcervoResult] = useState<{
    conn: TribunalConnectionView;
    data: AcervoPreviewResponse;
  } | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<AcervoImportResponse | null>(null);

  // Modal de edição
  const [editingConn, setEditingConn] = useState<TribunalConnectionView | null>(null);
  const [editForm, setEditForm] = useState<EditFormState | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const {
    data: connections = [],
    isLoading,
    error: loadError,
  } = useQuery({
    queryKey: ['tribunal-connections'],
    queryFn: () => apiClient.tribunalConnectionsList(),
    staleTime: 30_000,
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateTribunalConnectionInput) =>
      apiClient.tribunalConnectionsCreate(input),
    onSuccess: (conn) => {
      void qc.invalidateQueries({ queryKey: ['tribunal-connections'] });
      setModalOpen(false);
      setFeedback({
        type: 'ok',
        message: `Conexão ${connectionLabel(conn)} criada. Clique em "Reconectar" para verificar o login.`,
      });
    },
    onError: (e: ApiError) => {
      setFormError(e.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.tribunalConnectionsDelete(id),
    onSuccess: (_, id) => {
      qc.setQueryData<TribunalConnectionView[]>(
        ['tribunal-connections'],
        (prev) => prev?.filter((c) => c.id !== id) ?? [],
      );
      setFeedback({ type: 'ok', message: 'Conexão removida.' });
    },
    onError: (e: ApiError) => {
      setFeedback({ type: 'error', message: e.message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTribunalConnectionInput }) =>
      apiClient.tribunalConnectionsUpdate(id, input),
    onSuccess: (updated) => {
      qc.setQueryData<TribunalConnectionView[]>(
        ['tribunal-connections'],
        (prev) => prev?.map((c) => (c.id === updated.id ? updated : c)) ?? [],
      );
      setEditingConn(null);
      setEditForm(null);
      setFeedback({
        type: 'ok',
        message: `Conexão ${connectionLabel(updated)} atualizada. Clique em "Reconectar" para verificar o login.`,
      });
    },
    onError: (e: ApiError) => {
      setEditError(e.message);
    },
  });

  // Abre o modal pré-preenchido (sistema + tribunal + grau travados)
  function openConnectModal(
    sistema: FormState['sistema'],
    tribunalCode: string,
    grau: '1' | '2',
  ) {
    setForm({ ...EMPTY_FORM, sistema, tribunalCode, grau });
    setFormLocked({ sistema: true, tribunalCode: true, grau: true });
    setFormError(null);
    setModalOpen(true);
  }

  function openEditModal(conn: TribunalConnectionView) {
    setEditingConn(conn);
    setEditForm(emptyEditForm(conn));
    setEditError(null);
  }

  function handleEditSubmit() {
    if (!editingConn || !editForm) return;
    setEditError(null);

    if (!editForm.oabNumber.trim()) { setEditError('Informe o número da OAB.'); return; }
    if (editForm.totpSeed && !/^[A-Z2-7=\s]*$/i.test(editForm.totpSeed)) {
      setEditError('Seed do 2FA deve conter apenas caracteres base32 (A-Z e dígitos 2-7).');
      return;
    }

    const input: UpdateTribunalConnectionInput = {
      oabUf: editForm.oabUf,
      oabNumber: editForm.oabNumber.trim(),
    };
    if (editForm.loginUsername.trim() !== (editingConn.loginUsername ?? '')) {
      input.loginUsername = editForm.loginUsername.trim();
    }
    if (editForm.senha.trim()) {
      input.senha = editForm.senha;
    }
    // totpSeed: se usuário preencheu → atualiza; se deixou vazio E a conexão tinha TOTP → mantém (não envia)
    // se quiser remover o seed, basta deixar vazio — enviamos string vazia para o backend remover
    if (editForm.totpSeed.trim() || editingConn.hasTotpSeed) {
      input.totpSeed = editForm.totpSeed.trim().replace(/\s+/g, '').toUpperCase();
    }

    updateMutation.mutate({ id: editingConn.id, input });
  }

  async function handleTestLogin(conn: TribunalConnectionView) {
    setTestingId(conn.id);
    setFeedback(null);
    try {
      const result = await apiClient.tribunalConnectionsTestLogin(conn.id);
      void qc.invalidateQueries({ queryKey: ['tribunal-connections'] });
      setFeedback({
        type: result.success ? 'ok' : 'error',
        message: result.success
          ? `Login bem-sucedido em ${connectionLabel(conn)}. (${result.durationMs}ms)`
          : `Falha em ${connectionLabel(conn)}: ${result.message}`,
      });
    } catch (e) {
      setFeedback({ type: 'error', message: `Erro ao testar login: ${(e as ApiError).message}` });
    } finally {
      setTestingId(null);
    }
  }

  async function handleSyncAcervo(conn: TribunalConnectionView) {
    setAcervoLoadingId(conn.id);
    setFeedback(null);
    setImportResult(null);
    try {
      const data = await apiClient.tribunalConnectionsSyncAcervoPreview(conn.id);
      setAcervoResult({ conn, data });
    } catch (e) {
      setFeedback({
        type: 'error',
        message: `Erro ao sincronizar acervo: ${(e as ApiError).message}`,
      });
    } finally {
      setAcervoLoadingId(null);
    }
  }

  async function handleImportAcervo() {
    if (!acervoResult) return;
    setImporting(true);
    setFeedback(null);
    try {
      const result = await apiClient.tribunalConnectionsSyncAcervoImport(
        acervoResult.conn.id,
      );
      setImportResult(result);
      void qc.invalidateQueries({ queryKey: ['cases'] });
      setFeedback({
        type: 'ok',
        message: `Importação concluída: ${result.criados} criado(s), ${result.atualizados} atualizado(s), ${result.ignorados} ignorado(s).`,
      });
    } catch (e) {
      setFeedback({
        type: 'error',
        message: `Erro ao importar acervo: ${(e as ApiError).message}`,
      });
    } finally {
      setImporting(false);
    }
  }

  function closeAcervoModal() {
    setAcervoResult(null);
    setImportResult(null);
    setImporting(false);
  }

  async function handleDelete(conn: TribunalConnectionView) {
    if (!window.confirm(`Remover a conexão "${connectionLabel(conn)}"? Esta ação não pode ser desfeita.`)) return;
    deleteMutation.mutate(conn.id);
  }

  function handleSubmit() {
    setFormError(null);
    if (!form.tribunalCode) { setFormError('Tribunal inválido.'); return; }
    if (!form.oabNumber.trim()) { setFormError('Informe o número da OAB.'); return; }
    if (!form.senha) { setFormError('Informe a senha.'); return; }
    if (form.totpSeed && !/^[A-Z2-7=\s]+$/i.test(form.totpSeed)) {
      setFormError('Seed do 2FA deve conter apenas caracteres base32 (A-Z e dígitos 2-7).');
      return;
    }
    const input: CreateTribunalConnectionInput = {
      tribunalCode: form.tribunalCode,
      sistema: form.sistema,
      grau: parseInt(form.grau, 10) as 1 | 2,
      oabUf: form.oabUf,
      oabNumber: form.oabNumber.trim(),
      senha: form.senha,
    };
    if (form.loginUsername.trim()) input.loginUsername = form.loginUsername.trim();
    if (form.totpSeed.trim()) input.totpSeed = form.totpSeed.trim();
    createMutation.mutate(input);
  }

  // Conexões com problema para o banner do topo
  const problemConnections = connections.filter((c) => {
    const meta = getStatusMeta(c.status);
    return meta.severity === 'error' || (meta.severity === 'warn' && c.status !== 'pending');
  });

  // Mapa para lookup rápido: `${tribunalCode}:${grau}` -> conexão
  const connectionMap = new Map<string, TribunalConnectionView>();
  for (const conn of connections) {
    connectionMap.set(`${conn.tribunalCode}:${conn.grau}`, conn);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-[var(--fg)]">Conexões com Tribunais</h1>
        <p className="mt-1 max-w-2xl text-sm text-[var(--fg-muted)]">
          Conecte suas credenciais OAB aos sistemas judiciais eletrônicos. O VoxisAdv usará
          essas conexões para buscar movimentações e petições diretamente nos tribunais.
        </p>
      </div>

      {/* Feedback toast */}
      {feedback && (
        <div
          className={`rounded-md border px-4 py-3 text-sm ${
            feedback.type === 'ok'
              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
              : 'border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400'
          }`}
        >
          {feedback.message}
        </div>
      )}

      {/* Banner: precisam de atenção */}
      {!isLoading && problemConnections.length > 0 && (
        <section className="space-y-2">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-amber-500">
            <AlertTriangle className="h-4 w-4" />
            Precisam de atenção ({problemConnections.length})
          </h2>
          <div className="overflow-hidden rounded-lg border border-amber-500/20 bg-amber-500/5">
            <div className="divide-y divide-[var(--border)]">
              {problemConnections.map((conn) => (
                <AttentionRow
                  key={conn.id}
                  conn={conn}
                  isTesting={testingId === conn.id}
                  anyTesting={testingId !== null}
                  onTest={handleTestLogin}
                  onEdit={openEditModal}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Estado de carregamento */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-sm text-[var(--fg-muted)]">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Carregando conexões...
        </div>
      ) : loadError ? (
        <p className="py-8 text-center text-sm text-red-500">
          {(loadError as ApiError).message}
        </p>
      ) : (
        /* Catálogo por sistema processual */
        <div className="space-y-6">
          {CATALOG.map((sistema) => (
            <SistemaSection
              key={sistema.id}
              sistema={sistema}
              connectionMap={connectionMap}
              testingId={testingId}
              acervoLoadingId={acervoLoadingId}
              onConnect={openConnectModal}
              onTest={handleTestLogin}
              onSyncAcervo={handleSyncAcervo}
              onEdit={openEditModal}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Modal de nova conexão */}
      <ConfigModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Conectar ao tribunal"
        description="Informe as credenciais de acesso ao sistema eletrônico do tribunal."
        footer={
          <>
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={createMutation.isPending}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={createMutation.isPending}>
              {createMutation.isPending ? (
                <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Salvando...</>
              ) : (
                'Criar conexão'
              )}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {formError && (
            <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">{formError}</p>
          )}

          {/* Sistema + Tribunal + Grau (travados quando pré-preenchidos) */}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Sistema" required>
              {formLocked.sistema ? (
                <LockedField value={form.sistema.toUpperCase()} />
              ) : (
                <GlassSelect
                  value={form.sistema}
                  onChange={(v) => setForm((s) => ({ ...s, sistema: (v ?? 'eproc') as FormState['sistema'], tribunalCode: '' }))}
                  options={[
                    { value: 'eproc', label: 'eProc' },
                    { value: 'pje', label: 'PJe', disabled: true },
                    { value: 'esaj', label: 'eSAJ', disabled: true },
                    { value: 'projudi', label: 'Projudi', disabled: true },
                  ]}
                  placeholder="Sistema"
                />
              )}
            </Field>
            <Field label="Tribunal" required>
              {formLocked.tribunalCode ? (
                <LockedField value={form.tribunalCode} />
              ) : (
                <GlassSelect
                  value={form.tribunalCode || null}
                  onChange={(v) => setForm((s) => ({ ...s, tribunalCode: v ?? '' }))}
                  options={CATALOG.find((s) => s.id === form.sistema)?.tribunais.map((t) => ({
                    value: t.tribunalCode,
                    label: t.tribunalCode,
                  })) ?? []}
                  placeholder="Tribunal"
                />
              )}
            </Field>
            <Field label="Grau" required>
              {formLocked.grau ? (
                <LockedField value={`${form.grau}º Grau`} />
              ) : (
                <GlassSelect
                  value={form.grau}
                  onChange={(v) => setForm((s) => ({ ...s, grau: (v ?? '1') as '1' | '2' }))}
                  options={[
                    { value: '1', label: '1º Grau' },
                    { value: '2', label: '2º Grau' },
                  ]}
                  placeholder="Grau"
                />
              )}
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="OAB UF" required>
              <GlassSelect
                value={form.oabUf}
                onChange={(v) => setForm((s) => ({ ...s, oabUf: v ?? 'RS' }))}
                options={UF_OPTIONS}
                placeholder="UF"
              />
            </Field>
            <Field label="Número OAB" required>
              <TextInput
                value={form.oabNumber}
                onChange={(e) => setForm((s) => ({ ...s, oabNumber: e.target.value.replace(/\D/g, '') }))}
                placeholder="87113"
                inputMode="numeric"
                maxLength={10}
              />
            </Field>
          </div>

          <Field label="Usuário do sistema" required={form.sistema === 'pje'}>
            <TextInput
              value={form.loginUsername}
              onChange={(e) => setForm((s) => ({ ...s, loginUsername: e.target.value }))}
              placeholder={form.sistema === 'pje' ? 'CPF do advogado (só números)' : 'Ex: RS087113 ou CPF (opcional)'}
              autoComplete="username"
              inputMode={form.sistema === 'pje' ? 'numeric' : undefined}
            />
            <p className="mt-1 text-[11px] text-[var(--fg-muted)]">
              {form.sistema === 'pje'
                ? 'Informe o CPF do advogado (apenas números). O PJe não deriva o login da OAB.'
                : 'Deixe vazio pra usar UF+OAB (ex: RS087113). Preencha só se seu login no eProc for diferente — CPF ou usuário personalizado.'}
            </p>
          </Field>

          <Field label="Senha do sistema" required>
            <div className="relative">
              <TextInput
                type={form.showSenha ? 'text' : 'password'}
                value={form.senha}
                onChange={(e) => setForm((s) => ({ ...s, senha: e.target.value }))}
                placeholder="Senha de acesso ao eProc"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setForm((s) => ({ ...s, showSenha: !s.showSenha }))}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--fg-muted)] hover:text-[var(--fg)]"
                aria-label={form.showSenha ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {form.showSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </Field>

          <div>
            <p className="mb-1 flex items-center gap-1 text-xs font-medium text-[var(--fg-muted)]">
              Código de configuração do 2FA
              <span
                title="String base32 exibida junto ao QR code ao configurar o autenticador no eProc (ex: JBSWY3DPEHPK3PXP). Opcional."
                className="cursor-help"
              >
                <HelpCircle className="h-3.5 w-3.5" />
              </span>
            </p>
            <div className="relative">
              <TextInput
                type={form.showTotp ? 'text' : 'password'}
                value={form.totpSeed}
                onChange={(e) => setForm((s) => ({ ...s, totpSeed: e.target.value.toUpperCase() }))}
                placeholder="Cole o código de configuração (opcional)"
                autoComplete="off"
                className="font-mono"
              />
              <button
                type="button"
                onClick={() => setForm((s) => ({ ...s, showTotp: !s.showTotp }))}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--fg-muted)] hover:text-[var(--fg)]"
                aria-label={form.showTotp ? 'Ocultar seed' : 'Mostrar seed'}
              >
                {form.showTotp ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="mt-1 text-[11px] text-[var(--fg-muted)]">
              {form.sistema === 'pje'
                ? 'Geralmente não necessário no PJe. Preencha só se o tribunal exigir autenticador.'
                : 'É o texto exibido junto ao QR code ao configurar o autenticador no eProc. Não é o código de 6 dígitos que muda a cada 30s.'}
            </p>
          </div>
        </div>
      </ConfigModal>

      {/* Modal de edição de conexão existente */}
      <ConfigModal
        open={editingConn !== null}
        onClose={() => { setEditingConn(null); setEditForm(null); }}
        title="Editar conexão"
        description={editingConn ? `${connectionLabel(editingConn)} — atualize as credenciais.` : ''}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => { setEditingConn(null); setEditForm(null); }}
              disabled={updateMutation.isPending}
            >
              Cancelar
            </Button>
            <Button onClick={handleEditSubmit} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? (
                <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Salvando...</>
              ) : (
                'Salvar alterações'
              )}
            </Button>
          </>
        }
      >
        {editForm && editingConn && (
          <div className="flex flex-col gap-4">
            {editError && (
              <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">{editError}</p>
            )}

            {/* Campos identificadores — travados */}
            <div className="grid grid-cols-3 gap-3">
              <Field label="Sistema">
                <LockedField value={editingConn.sistema.toUpperCase()} />
              </Field>
              <Field label="Tribunal">
                <LockedField value={editingConn.tribunalCode} />
              </Field>
              <Field label="Grau">
                <LockedField value={`${editingConn.grau}º Grau`} />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="OAB UF" required>
                <GlassSelect
                  value={editForm.oabUf}
                  onChange={(v) => setEditForm((s) => s ? { ...s, oabUf: v ?? 'RS' } : s)}
                  options={UF_OPTIONS}
                  placeholder="UF"
                />
              </Field>
              <Field label="Número OAB" required>
                <TextInput
                  value={editForm.oabNumber}
                  onChange={(e) => setEditForm((s) => s ? { ...s, oabNumber: e.target.value.replace(/\D/g, '') } : s)}
                  placeholder="87113"
                  inputMode="numeric"
                  maxLength={10}
                />
              </Field>
            </div>

            <Field label="Usuário do sistema">
              <TextInput
                value={editForm.loginUsername}
                onChange={(e) => setEditForm((s) => s ? { ...s, loginUsername: e.target.value } : s)}
                placeholder="Deixe vazio pra usar UF+OAB"
                autoComplete="username"
              />
            </Field>

            <Field label="Nova senha">
              <div className="relative">
                <TextInput
                  type={editForm.showSenha ? 'text' : 'password'}
                  value={editForm.senha}
                  onChange={(e) => setEditForm((s) => s ? { ...s, senha: e.target.value } : s)}
                  placeholder="Deixe vazio para manter a senha atual"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setEditForm((s) => s ? { ...s, showSenha: !s.showSenha } : s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--fg-muted)] hover:text-[var(--fg)]"
                  aria-label={editForm.showSenha ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {editForm.showSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="mt-1 text-[11px] text-[var(--fg-muted)]">
                Deixe em branco para manter a senha atual.
              </p>
            </Field>

            <div>
              <p className="mb-1 flex items-center gap-1 text-xs font-medium text-[var(--fg-muted)]">
                Código de configuração do 2FA
                <span
                  title="String base32 exibida junto ao QR code ao configurar o autenticador (ex: JBSWY3DPEHPK3PXP)."
                  className="cursor-help"
                >
                  <HelpCircle className="h-3.5 w-3.5" />
                </span>
                {editingConn.hasTotpSeed && (
                  <span className="ml-1 rounded-full bg-blue-500/15 px-1.5 py-0.5 text-[10px] font-medium text-blue-500">
                    configurado
                  </span>
                )}
              </p>
              <div className="relative">
                <TextInput
                  type={editForm.showTotp ? 'text' : 'password'}
                  value={editForm.totpSeed}
                  onChange={(e) => setEditForm((s) => s ? { ...s, totpSeed: e.target.value.toUpperCase() } : s)}
                  placeholder={
                    editingConn.hasTotpSeed
                      ? 'Novo seed (vazio = manter atual; espaço+delete = remover)'
                      : 'Cole o código de configuração (opcional)'
                  }
                  autoComplete="off"
                  className="font-mono"
                />
                <button
                  type="button"
                  onClick={() => setEditForm((s) => s ? { ...s, showTotp: !s.showTotp } : s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--fg-muted)] hover:text-[var(--fg)]"
                  aria-label={editForm.showTotp ? 'Ocultar seed' : 'Mostrar seed'}
                >
                  {editForm.showTotp ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="mt-1 text-[11px] text-[var(--fg-muted)]">
                {editingConn.hasTotpSeed
                  ? 'Deixe vazio para manter o seed atual. Para remover o 2FA, limpe o campo e salve.'
                  : 'É o texto exibido junto ao QR code ao configurar o autenticador. Não é o código de 6 dígitos.'}
              </p>
            </div>
          </div>
        )}
      </ConfigModal>

      {/* Modal de resultado da prévia do acervo */}
      <ConfigModal
        open={acervoResult !== null}
        onClose={closeAcervoModal}
        title="Prévia do acervo"
        description={
          acervoResult
            ? importResult
              ? `${connectionLabel(acervoResult.conn)} — importação concluída.`
              : `${connectionLabel(acervoResult.conn)} — revise e importe os processos.`
            : ''
        }
        footer={
          <>
            <Button variant="outline" onClick={closeAcervoModal} disabled={importing}>
              Fechar
            </Button>
            {acervoResult && !importResult && acervoResult.data.total > 0 && (
              <Button onClick={() => void handleImportAcervo()} disabled={importing}>
                {importing ? (
                  <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Importando...</>
                ) : (
                  `Importar ${acervoResult.data.total} processo${acervoResult.data.total > 1 ? 's' : ''}`
                )}
              </Button>
            )}
          </>
        }
      >
        {acervoResult && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="rounded-full bg-zinc-500/15 px-2 py-0.5 font-medium text-[var(--fg)]">
                Total: {acervoResult.data.total}
              </span>
              <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 font-medium text-emerald-500">
                Novos: {acervoResult.data.novos}
              </span>
              <span className="rounded-full bg-blue-500/15 px-2 py-0.5 font-medium text-blue-500">
                Já existem: {acervoResult.data.existentes}
              </span>
              {acervoResult.data.reauthenticated && (
                <span className="rounded-full bg-amber-500/15 px-2 py-0.5 font-medium text-amber-500">
                  Re-autenticou
                </span>
              )}
            </div>

            {importResult && (
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300">
                <p className="font-medium">Importação concluída</p>
                <p className="mt-0.5">
                  {importResult.criados} criado(s) · {importResult.atualizados} atualizado(s) ·{' '}
                  {importResult.ignorados} ignorado(s)
                </p>
              </div>
            )}

            {acervoResult.data.note && (
              <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
                {acervoResult.data.note}
              </p>
            )}

            {acervoResult.data.processos.length === 0 ? (
              <p className="py-6 text-center text-sm text-[var(--fg-muted)]">
                Nenhum processo extraído. O markup do acervo foi capturado no
                servidor (debug) para ajustar os seletores.
              </p>
            ) : (
              <div className="max-h-[55vh] divide-y divide-[var(--border)] overflow-y-auto rounded-md border border-[var(--border)]">
                {acervoResult.data.processos.map((p, idx) => (
                  <div key={`${p.numeroCnjDigits ?? idx}`} className="px-3 py-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[var(--fg)]">
                        {p.numeroCnj ?? p.numeroCnjDigits ?? '(sem número)'}
                      </span>
                      {p.jaExiste ? (
                        <span className="rounded-full bg-blue-500/15 px-1.5 py-0.5 text-[10px] font-medium text-blue-500">
                          já existe
                        </span>
                      ) : (
                        <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-500">
                          novo
                        </span>
                      )}
                    </div>
                    {(p.classe || p.orgaoJulgador) && (
                      <div className="mt-0.5 text-xs text-[var(--fg-muted)]">
                        {[p.classe, p.orgaoJulgador].filter(Boolean).join(' · ')}
                      </div>
                    )}
                    {p.partes.length > 0 && (
                      <div className="mt-0.5 text-xs text-[var(--fg-muted)]">
                        {p.partes.map((pt) => pt.nome).join(' x ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </ConfigModal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Campo travado (read-only visual)
// ---------------------------------------------------------------------------

function LockedField({ value }: { value: string }) {
  return (
    <div className="flex h-9 items-center rounded-md border border-[var(--border)] bg-[var(--surface-2,var(--surface))] px-3 text-sm text-[var(--fg-muted)] opacity-70 select-none">
      {value}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Seção de sistema processual
// ---------------------------------------------------------------------------

interface SistemaSectionProps {
  sistema: CatalogSistema;
  connectionMap: Map<string, TribunalConnectionView>;
  testingId: string | null;
  acervoLoadingId: string | null;
  onConnect: (sistema: FormState['sistema'], tribunalCode: string, grau: '1' | '2') => void;
  onTest: (conn: TribunalConnectionView) => Promise<void>;
  onSyncAcervo: (conn: TribunalConnectionView) => Promise<void>;
  onEdit: (conn: TribunalConnectionView) => void;
  onDelete: (conn: TribunalConnectionView) => void;
}

function SistemaSection({
  sistema,
  connectionMap,
  testingId,
  acervoLoadingId,
  onConnect,
  onTest,
  onSyncAcervo,
  onEdit,
  onDelete,
}: SistemaSectionProps) {
  const connectedCount = sistema.tribunais.filter(
    (t) => connectionMap.has(`${t.tribunalCode}:${t.grau}`),
  ).length;

  return (
    <section className="space-y-2">
      {/* Cabeçalho da seção */}
      <div className="flex items-center gap-2">
        <h2 className="text-base font-semibold text-[var(--fg)]">{sistema.label}</h2>
        {sistema.enabled ? (
          connectedCount > 0 ? (
            <span className="inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-500">
              {connectedCount} conectado{connectedCount > 1 ? 's' : ''}
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-zinc-500/15 px-2 py-0.5 text-xs font-medium text-zinc-400">
              Disponível
            </span>
          )
        ) : (
          <span className="inline-flex items-center rounded-full bg-zinc-500/10 px-2 py-0.5 text-xs font-medium text-zinc-500">
            Em breve
          </span>
        )}
      </div>

      {/* Lista de tribunais */}
      <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
        <div className="divide-y divide-[var(--border)]">
          {sistema.tribunais.map((tribunal) => {
            const conn = connectionMap.get(`${tribunal.tribunalCode}:${tribunal.grau}`);
            return (
              <TribunalRow
                key={`${tribunal.tribunalCode}:${tribunal.grau}`}
                tribunal={tribunal}
                sistema={sistema}
                conn={conn ?? null}
                testingId={testingId}
                acervoLoadingId={acervoLoadingId}
                onConnect={onConnect}
                onTest={onTest}
                onSyncAcervo={onSyncAcervo}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Linha de tribunal no catálogo
// ---------------------------------------------------------------------------

interface TribunalRowProps {
  tribunal: CatalogTribunal;
  sistema: CatalogSistema;
  conn: TribunalConnectionView | null;
  testingId: string | null;
  acervoLoadingId: string | null;
  onConnect: (sistema: FormState['sistema'], tribunalCode: string, grau: '1' | '2') => void;
  onTest: (conn: TribunalConnectionView) => Promise<void>;
  onSyncAcervo: (conn: TribunalConnectionView) => Promise<void>;
  onEdit: (conn: TribunalConnectionView) => void;
  onDelete: (conn: TribunalConnectionView) => void;
}

function TribunalRow({
  tribunal,
  sistema,
  conn,
  testingId,
  acervoLoadingId,
  onConnect,
  onTest,
  onSyncAcervo,
  onEdit,
  onDelete,
}: TribunalRowProps) {
  const isDisabledSistema = !sistema.enabled;
  const isTesting = conn ? testingId === conn.id : false;
  const anyTesting = testingId !== null;
  const isSyncingAcervo = conn ? acervoLoadingId === conn.id : false;
  const anyAcervoLoading = acervoLoadingId !== null;

  const meta = getStatusMeta(conn?.status ?? null);
  const StatusIcon = meta.icon;

  return (
    <div
      className={`flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4 ${
        isDisabledSistema ? 'opacity-45' : ''
      }`}
    >
      {/* Identidade do tribunal */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-medium ${isDisabledSistema ? 'text-[var(--fg-muted)]' : 'text-[var(--fg)]'}`}>
            {tribunal.label}
          </span>

          {/* Badge de status */}
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
              isDisabledSistema ? 'bg-zinc-500/10 text-zinc-500' : meta.colorClass
            }`}
          >
            {isDisabledSistema ? (
              'Em breve'
            ) : (
              <>
                <StatusIcon className="h-3 w-3" />
                {meta.label}
              </>
            )}
          </span>

          {/* Badge 2FA */}
          {conn?.hasTotpSeed && (
            <span className="inline-flex items-center rounded-full bg-blue-500/15 px-2 py-0.5 text-xs font-medium text-blue-500">
              2FA
            </span>
          )}
        </div>

        {/* Sub-info quando conectado */}
        {conn && !isDisabledSistema && (
          <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-[var(--fg-muted)]">
            <span>OAB {conn.oabNumber}/{conn.oabUf}</span>
            {conn.lastCheckAt && (
              <span>Última verificação: {formatDateTime(conn.lastCheckAt)}</span>
            )}
            {conn.lastError && meta.severity !== 'ok' && (
              <span className="text-red-400" title={conn.lastError}>
                {conn.lastError.length > 60 ? `${conn.lastError.slice(0, 60)}...` : conn.lastError}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Ações */}
      {!isDisabledSistema && (
        <div className="flex shrink-0 items-center gap-2">
          {conn ? (
            <>
              {conn.status === 'active' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void onSyncAcervo(conn)}
                  disabled={anyTesting || anyAcervoLoading}
                  title="Buscar a lista de processos do acervo (prévia — não importa nada)"
                >
                  {isSyncingAcervo ? (
                    <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /><span className="hidden sm:inline">Buscando acervo...</span></>
                  ) : (
                    <><FolderSync className="h-3.5 w-3.5" /><span className="ml-1.5 hidden sm:inline">Sincronizar acervo (prévia)</span></>
                  )}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => void onTest(conn)}
                disabled={anyTesting || anyAcervoLoading}
                title={isTesting ? 'Conectando ao tribunal...' : 'Testar login agora'}
              >
                {isTesting ? (
                  <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /><span className="hidden sm:inline">Conectando...</span></>
                ) : (
                  <><RefreshCw className="h-3.5 w-3.5" /><span className="ml-1.5 hidden sm:inline">Reconectar</span></>
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(conn)}
                disabled={anyTesting || anyAcervoLoading}
                className="text-[var(--fg-muted)] hover:text-[var(--fg)]"
                title="Editar credenciais da conexão"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void onDelete(conn)}
                disabled={anyTesting || anyAcervoLoading}
                className="text-[var(--fg-muted)] hover:text-red-500"
                title="Remover conexão"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                onConnect(sistema.id, tribunal.tribunalCode, String(tribunal.grau) as '1' | '2')
              }
            >
              <Link2 className="mr-1.5 h-3.5 w-3.5" />
              Conectar
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Linha do banner "Precisam de atenção"
// ---------------------------------------------------------------------------

interface AttentionRowProps {
  conn: TribunalConnectionView;
  isTesting: boolean;
  anyTesting: boolean;
  onTest: (conn: TribunalConnectionView) => Promise<void>;
  onEdit: (conn: TribunalConnectionView) => void;
  onDelete: (conn: TribunalConnectionView) => void;
}

function AttentionRow({ conn, isTesting, anyTesting, onTest, onEdit, onDelete }: AttentionRowProps) {
  const meta = getStatusMeta(conn.status);
  const StatusIcon = meta.icon;

  return (
    <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-[var(--fg)]">{connectionLabel(conn)}</span>
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${meta.colorClass}`}>
            <StatusIcon className="h-3 w-3" />
            {meta.label}
          </span>
        </div>
        <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-[var(--fg-muted)]">
          <span>OAB {conn.oabNumber}/{conn.oabUf}</span>
          {conn.lastCheckAt && <span>Última verificação: {formatDateTime(conn.lastCheckAt)}</span>}
          {conn.lastError && (
            <span className="text-red-400" title={conn.lastError}>
              {conn.lastError.length > 60 ? `${conn.lastError.slice(0, 60)}...` : conn.lastError}
            </span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => void onTest(conn)} disabled={anyTesting}>
          {isTesting ? (
            <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /><span className="hidden sm:inline">Conectando...</span></>
          ) : (
            <><RefreshCw className="h-3.5 w-3.5" /><span className="ml-1.5 hidden sm:inline">Reconectar</span></>
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(conn)}
          disabled={anyTesting}
          className="text-[var(--fg-muted)] hover:text-[var(--fg)]"
          title="Editar credenciais da conexão"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void onDelete(conn)}
          disabled={anyTesting}
          className="text-[var(--fg-muted)] hover:text-red-500"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
